import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import SiteGate from "@/components/SiteGate";
import LoginPage from "@/pages/LoginPage";
import AppLayout from "@/components/AppLayout";
import DashboardPage from "@/pages/DashboardPage";
import ModulePage from "@/pages/ModulePage";
import RecordViewPage from "@/pages/RecordViewPage";
import RecordEditPage from "@/pages/RecordEditPage";
import SalesDashboardPage from "@/pages/SalesDashboardPage";
import PurchaseDashboardPage from "@/pages/PurchaseDashboardPage";
import DispatchDashboardPage from "@/pages/DispatchDashboardPage";
import AccountsDashboardPage from "@/pages/AccountsDashboardPage";
import ProductDashboardPage from "@/pages/ProductDashboardPage";
import TenderDashboardPage from "@/pages/TenderDashboardPage";
import ProductionDashboardPage from "@/pages/ProductionDashboardPage";
import AccessMatrixPage from "@/pages/AccessMatrixPage";
import StaffPage from "@/pages/StaffPage";
import AIReportPage from "@/pages/AIReportPage";
import ActivityLogPage from "@/pages/ActivityLogPage";
import FieldExpensePage from "@/pages/FieldExpensePage";
import NotificationsPage from "@/pages/NotificationsPage";
import PbgLedgerPage from "@/pages/PbgLedgerPage";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="h-7 w-7 text-slate-400 animate-spin" />
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="h-7 w-7 text-slate-400 animate-spin" />
    </div>
  );
  if (user) return <Navigate to="/" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (!user) return null;
  if (user.role !== "admin" && user.role !== "dept_head") {
    return <Navigate to="/" replace />;
  }
  return children;
}

function App() {
  return (
    <SiteGate>
      <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
          <Route
            path="/"
            element={
              <Protected>
                <AppLayout><DashboardPage /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/m/:moduleKey"
            element={
              <Protected>
                <AppLayout><ModulePage /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/m/:moduleKey/:recordId"
            element={
              <Protected>
                <AppLayout><RecordViewPage /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/m/:moduleKey/:recordId/edit"
            element={
              <Protected>
                <AppLayout><RecordEditPage /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/sales-dashboard"
            element={<Protected><AppLayout><SalesDashboardPage /></AppLayout></Protected>}
          />
          <Route
            path="/purchase-dashboard"
            element={<Protected><AppLayout><PurchaseDashboardPage /></AppLayout></Protected>}
          />
          <Route
            path="/dispatch-dashboard"
            element={<Protected><AppLayout><DispatchDashboardPage /></AppLayout></Protected>}
          />
          <Route
            path="/accounts-dashboard"
            element={<Protected><AppLayout><AccountsDashboardPage /></AppLayout></Protected>}
          />
          <Route
            path="/product-dashboard"
            element={<Protected><AppLayout><ProductDashboardPage /></AppLayout></Protected>}
          />
          <Route
            path="/tender-dashboard"
            element={<Protected><AppLayout><TenderDashboardPage /></AppLayout></Protected>}
          />
          <Route
            path="/production-dashboard"
            element={<Protected><AppLayout><ProductionDashboardPage /></AppLayout></Protected>}
          />
          <Route
            path="/access-matrix"
            element={
              <Protected>
                <AppLayout><AccessMatrixPage /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/staff"
            element={
              <Protected><AdminOnly><AppLayout><StaffPage /></AppLayout></AdminOnly></Protected>
            }
          />
          <Route
            path="/ai-report"
            element={
              <Protected><AdminOnly><AppLayout><AIReportPage /></AppLayout></AdminOnly></Protected>
            }
          />
          <Route
            path="/activity"
            element={
              <Protected><AdminOnly><AppLayout><ActivityLogPage /></AppLayout></AdminOnly></Protected>
            }
          />
          <Route
            path="/notifications"
            element={
              <Protected><AdminOnly><AppLayout><NotificationsPage /></AppLayout></AdminOnly></Protected>
            }
          />
          <Route
            path="/pbg-ledger"
            element={
              <Protected><AppLayout><PbgLedgerPage /></AppLayout></Protected>
            }
          />
          <Route
            path="/field/expense"
            element={
              <Protected><FieldExpensePage /></Protected>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
    </SiteGate>
  );
}

export default App;
